/** PKCE (RFC 7636) para OAuth VIDaaS / ITI CAS. */

import { SYNAP_ITI_OAUTH_STORAGE_KEY } from '@/shared/lib/itiOAuth'
import { prefs } from '@/shared/lib/platform/preferences'
import { SYNAP_VIDAAS_OAUTH_STORAGE_KEY } from '@/shared/lib/vidaasOAuth'

export const OAUTH_PKCE_VERIFIER_KEY = 'oauth_pkce_code_verifier'
export const OAUTH_PKCE_STATE_KEY = 'oauth_pkce_state'

export type OAuthPkceProvider = 'iti' | 'vidaas'

export interface OAuthPkceSession {
  verifier: string
  state: string
  provider: OAuthPkceProvider
}

export interface OAuthPkceCallbackResult {
  handled: boolean
  provider: OAuthPkceProvider | null
  ok: boolean
  state: string
  code?: string
  error?: string
  verifier?: string
}

const randomBytes = (n: number): Uint8Array => {
  const u = new Uint8Array(n)
  crypto.getRandomValues(u)
  return u
}

export const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!)
  const b64 = btoa(binary)
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const generateCodeVerifier = (): string => base64UrlEncode(randomBytes(32))

export const codeChallengeS256 = async (verifier: string): Promise<string> => {
  const enc = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', enc)
  return base64UrlEncode(new Uint8Array(digest))
}

const sessionStorageKey = (base: string, provider: OAuthPkceProvider): string =>
  `${base}:${provider}`

/** Persiste verifier/state (Preferences nativo; localStorage namespaced na PWA + sessionStorage fallback). */
export const savePkceSession = async (session: OAuthPkceSession): Promise<void> => {
  const payload = JSON.stringify(session)
  await prefs.set(`${OAUTH_PKCE_VERIFIER_KEY}:${session.provider}`, session.verifier)
  await prefs.set(`${OAUTH_PKCE_STATE_KEY}:${session.provider}`, session.state)
  await prefs.set(`oauth_pkce_session:${session.provider}`, payload)

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(sessionStorageKey(OAUTH_PKCE_VERIFIER_KEY, session.provider), session.verifier)
    sessionStorage.setItem(sessionStorageKey(OAUTH_PKCE_STATE_KEY, session.provider), session.state)
  }
}

export const loadPkceSession = async (provider: OAuthPkceProvider): Promise<OAuthPkceSession | null> => {
  const fromPrefsVerifier = await prefs.get(`${OAUTH_PKCE_VERIFIER_KEY}:${provider}`)
  const fromPrefsState = await prefs.get(`${OAUTH_PKCE_STATE_KEY}:${provider}`)
  if (fromPrefsVerifier && fromPrefsState) {
    return { verifier: fromPrefsVerifier, state: fromPrefsState, provider }
  }

  const rawSession = await prefs.get(`oauth_pkce_session:${provider}`)
  if (rawSession) {
    try {
      const parsed = JSON.parse(rawSession) as OAuthPkceSession
      if (parsed.verifier && parsed.state) {
        return { verifier: parsed.verifier, state: parsed.state, provider }
      }
    } catch {
      /* ignore */
    }
  }

  if (typeof sessionStorage !== 'undefined') {
    const verifier = sessionStorage.getItem(sessionStorageKey(OAUTH_PKCE_VERIFIER_KEY, provider))
    const state = sessionStorage.getItem(sessionStorageKey(OAUTH_PKCE_STATE_KEY, provider))
    if (verifier && state) {
      return { verifier, state, provider }
    }
  }

  return null
}

export const clearPkceSession = async (provider: OAuthPkceProvider): Promise<void> => {
  await prefs.remove(`${OAUTH_PKCE_VERIFIER_KEY}:${provider}`)
  await prefs.remove(`${OAUTH_PKCE_STATE_KEY}:${provider}`)
  await prefs.remove(`oauth_pkce_session:${provider}`)

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(sessionStorageKey(OAUTH_PKCE_VERIFIER_KEY, provider))
    sessionStorage.removeItem(sessionStorageKey(OAUTH_PKCE_STATE_KEY, provider))
  }
}

const detectProviderFromCallbackUrl = (url: URL): OAuthPkceProvider | null => {
  const path = url.pathname
  if (path.includes('/oauth/iti-cas/callback') || path.includes('/auth/iti/callback')) {
    return 'iti'
  }
  if (path.includes('/oauth/vidaas/callback') || path.includes('/auth/vidaas/callback')) {
    return 'vidaas'
  }
  return null
}

const oauthResultStorageKey = (provider: OAuthPkceProvider): string =>
  provider === 'iti' ? SYNAP_ITI_OAUTH_STORAGE_KEY : SYNAP_VIDAAS_OAUTH_STORAGE_KEY

const writeOAuthResult = (
  provider: OAuthPkceProvider,
  payload: { state: string; code: string; error: string },
): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(
      oauthResultStorageKey(provider),
      JSON.stringify({
        state: payload.state,
        code: payload.code,
        error: payload.error,
        at: Date.now(),
      }),
    )
  } catch {
    /* quota / private mode */
  }
}

/**
 * Processa retorno OAuth via deep link (nativo) ou URL de callback.
 * Valida `state`, grava resultado em localStorage para o fluxo de assinatura e limpa PKCE persistido.
 */
export const handleCallback = async (url: URL): Promise<OAuthPkceCallbackResult> => {
  const provider = detectProviderFromCallbackUrl(url)
  if (!provider) {
    return { handled: false, provider: null, ok: false, state: '' }
  }

  const state = url.searchParams.get('state') ?? ''
  const code = url.searchParams.get('code') ?? ''
  const error =
    url.searchParams.get('error') ??
    url.searchParams.get('error_description') ??
    ''

  const session = await loadPkceSession(provider)
  if (!session || !state || session.state !== state) {
    await clearPkceSession(provider)
    const mismatchError = session ? 'state_mismatch' : 'missing_pkce_session'
    writeOAuthResult(provider, { state, code: '', error: mismatchError })
    return {
      handled: true,
      provider,
      ok: false,
      state,
      error: mismatchError,
    }
  }

  writeOAuthResult(provider, { state, code, error })
  await clearPkceSession(provider)

  try {
    const { closeBrowser } = await import('@/shared/lib/platform/browser')
    await closeBrowser()
  } catch {
    /* web / Browser plugin indisponível */
  }

  return {
    handled: true,
    provider,
    ok: !error && Boolean(code),
    state,
    code: code || undefined,
    error: error || undefined,
    verifier: session.verifier,
  }
}
